import React, { useState, useEffect, useMemo } from "react";
import { FormGroup } from "reactstrap";
import "../../styles/Filter.css";
import { useDispatch, useSelector } from "react-redux";
import {
  getStates,
  getDistricts,
  getLevelThree,
} from "../../services/FilterAPI.tsx";
import StateFilter from "./StateFilter.tsx";
import {
  clearDistrictArray,
  clearLevelThreeArray,
  updateActiveView,
} from "../../redux/slices/filterSectionSlice.ts";
import {
  ACTIVE_VIEW_COUNTRY,
  ACTIVE_VIEW_STATE,
  ALL_PRODUCT,
  ALL_CROPS,
  COUNTRY,
  DATE_FORMAT,
  ACTIVE_VIEW_CONSOLIDATED,
} from "../../utils/Constants.tsx";
import Up from "../../assets/images/arrow_up.svg";
import Down from "../../assets/images/arrow_down.svg";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getDataWithKeyValue, getSeasonDateRange } from "../../utils/Util.tsx";
import { OptionType } from "../../utils/Types.tsx";
import { handleStateViewData } from "../../redux/slices/mapApiSlice.ts";

export interface FilterFormType {
  state: string;
  district: string;
  distance: number;
  countryView: string;
  gridNumber: string;
  styleView: string;
  startDate: string;
  endDate: string;
  stateStartDate: string | null;
  stateEndDate: string | null;
  levelOne: string;
  levelTwo: string;
  levelThree: string;
  product: string;

  // multi select
  season: OptionType[];
  year: string;

  mapView: string;
  claimsCategory: string | null;
}

export const defaultFilter: FilterFormType = {
  state: COUNTRY,
  district: "",
  distance: 0,
  countryView: "",
  gridNumber: "",
  styleView: "",
  startDate: moment().subtract(1, "days").format(DATE_FORMAT.DD_MM_YYYY),
  endDate: moment().format(DATE_FORMAT.DD_MM_YYYY),
  stateStartDate: moment().subtract(1, "days").format(DATE_FORMAT.DD_MM_YYYY),
  stateEndDate: moment().format(DATE_FORMAT.DD_MM_YYYY),
  levelOne: "",
  levelTwo: "",
  levelThree: "",
  product: ALL_PRODUCT,

  season: [],
  year: "",

  mapView: "",
  claimsCategory: null,
};

interface SelectedRow {
  state?: string;
  district?: string;
}
interface FilterSectionProps {
  onApplyFilters: (data: any) => void;
  onClearGridId: () => void;
  gridId: string;
  onDistanceRangeChange: (value: number) => void;
  onStateChange: (value: string) => void;
  selectedRow?: SelectedRow;
}

const districtPH = "Select District";
const level3PH = "Select Level 3";

const FilterSection: React.FC<FilterSectionProps> = ({
  onApplyFilters,
  onClearGridId,
  gridId,
  onDistanceRangeChange,
  onStateChange,
  selectedRow,
}) => {
  const [formData, setFormData] = useState<typeof defaultFilter>(defaultFilter);
  // Category kept outside formData
  const [claimsCategory, setClaimsCategory] = useState<string | null>(null);

  const [error, setError] = useState<string>("");
  const [districtList, setDistricts] = useState<string[]>([]);
  const [statePlaceholder, setStatePlaceholder] = useState<string>(COUNTRY);
  const [districtPlaceholder, setDistrictPlaceholder] =
    useState<string>(districtPH);
  const [level3Placeholder, setLevel3Placeholder] = useState<string>(level3PH);

  const dispatch: any = useDispatch();

  const stateList = [
    COUNTRY,
    ...useSelector((state: any) => state?.filter?.statesData),
  ];
  const seasons = useSelector((state: any) => state?.filter?.seasons);
  const statesWithId = useSelector(
    (state: any) => state?.filter?.statesDataWithId,
  );
  const districtsData = useSelector(
    (state: any) => state?.filter?.districtsData,
  );

  const [isExpanded, setExpanded] = useState(true);
  const [minDate, setMinDate] = useState<Date | string | null>(null);
  const [maxDate, setMaxDate] = useState<Date | string | null>(
    moment().toDate(),
  );

  const navigate = useNavigate();

  useEffect(() => {
    onStateChange(COUNTRY);
  }, [onStateChange]);

  useEffect(() => {
    dispatch(getStates());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedRow) return;
    setFormData((prev) => ({
      ...prev,
      state: selectedRow.state ?? prev.state,
      district: selectedRow.district ?? prev.district,
    }));
    if (selectedRow.state) setStatePlaceholder(selectedRow.state);
    if (selectedRow.district) setDistrictPlaceholder(selectedRow.district);
  }, [selectedRow]);

  useEffect(() => {
    setDistricts(districtsData || []);
  }, [districtsData]);

  const handleStateChangeLocal = (state: string) => {
    setFormData((prev) => ({
      ...prev,
      state,
      district: "",
      gridNumber: "",
      distance: 0,
    }));
    onStateChange(state);
    setStatePlaceholder(state);
    setDistrictPlaceholder(districtPH);
    setLevel3Placeholder(level3PH);
    setDistricts([]);

    if (state) {
      if (state !== COUNTRY) {
        dispatch(
          getDistricts(getDataWithKeyValue(statesWithId, "vstate", state)?.sid),
        );
      }
      dispatch(
        getLevelThree(getDataWithKeyValue(statesWithId, "vstate", state)?.sid),
      );
    } else {
      dispatch(clearDistrictArray([]));
      dispatch(clearLevelThreeArray([]));
    }
    // reset levelThree
    handleChange({ target: { name: "levelThree", value: "" } });
  };

  const handleDistrictChangeLocal = (district: string) => {
    setFormData((prev) => ({ ...prev, district, gridNumber: "", distance: 0 }));
    setDistrictPlaceholder(district || districtPH);
  };

  const clearFilters = () => {
    setFormData(defaultFilter);
    setStatePlaceholder(COUNTRY);
    setDistrictPlaceholder(districtPH);
    setLevel3Placeholder(level3PH);
    setDistricts([]);
    setClaimsCategory(null);
    setError("");
    onApplyFilters({ ...defaultFilter, claimsCategory: null });
    onStateChange(COUNTRY);
    dispatch(clearDistrictArray([]));
    dispatch(clearLevelThreeArray([]));
    dispatch(handleStateViewData([]));
    dispatch(handleStateViewData([]));
  };

  const handleDateChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const onSeasonAndYearChange = () => {
    const selected = Array.isArray(formData?.season) ? formData.season : [];
    const seasonDateRange = getSeasonDateRange(formData, seasons);

    if (seasonDateRange) {
      // Keep range constraints updated
      setMinDate(seasonDateRange?.minDate);
      setMaxDate(seasonDateRange?.maxDate);

      // Do NOT overwrite dates when no season is selected
      setFormData((prev) => ({
        ...prev,
        stateStartDate: selected.length
          ? seasonDateRange?.stateStartDate
          : prev.stateStartDate, // keep previous date
        stateEndDate: selected.length
          ? seasonDateRange?.stateEndDate
          : prev.stateEndDate, // keep previous date
      }));
    }
  };

  useEffect(() => {
    onSeasonAndYearChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.season, formData?.year]);

  const levelOneLower = (formData.levelOne || "").toString().toLowerCase();
  const isPOSPM = levelOneLower === "pospm";

  const level3Disabled = useMemo(() => {
    if (isPOSPM) return true;
    if (levelOneLower === "policy" || levelOneLower === "claims") {
      return false;
    }
    return false;
  }, [isPOSPM, levelOneLower]);

  
  const resetSeasonYearAndDates = () => {
    const today = moment().format(DATE_FORMAT.DD_MM_YYYY);
    const yesterday = moment().subtract(1, "days").format(DATE_FORMAT.DD_MM_YYYY);

    // Reset date range constraints too
    setMinDate(null);
    setMaxDate(moment().toDate());

    setFormData((prev) => ({
      ...prev,
      season: [],
      year: "",
      startDate: yesterday,
      endDate: today,
      stateStartDate: yesterday,
      stateEndDate: today,
    }));
  };

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | { target: { name: string; value: any; type?: string } },
  ) => {
    const { name, value } = (e as any).target;

    // --- When Level 1 changes: reset Season/Year/Dates and L2 (and optionally L3) ---
    if (name === "levelOne") {
      const lower = (value || "").toString().toLowerCase();
      const nextIsPOSPM = lower === "pospm";

      // Reset Season/Year/Dates whenever Level 1 changes
      resetSeasonYearAndDates();

      setFormData((prev) => {
        const next = {
          ...prev,
          levelOne: value,
          levelTwo: "", // ALWAYS reset L2 on Level 1 change
          distance: 0,
        } as typeof prev;

        if (nextIsPOSPM) {
          next.levelThree = "";
          setLevel3Placeholder(level3PH);
        }
        // Clear claims category when Level 1 changes
        setClaimsCategory(null);

        return next;
      });
      return;
    }

    if (name === "season") {
      setFormData((prev) => ({
        ...prev,
        season: Array.isArray(value) ? value : [],
        distance: 0,
      }));
      return;
    }

    // --- When Level 3 changes: update L3 and reset YEAR (optional season clear) ---
    if (name === "levelThree") {
      if (level3Disabled) return;
      setFormData((prev) => ({
        ...prev,
        levelThree: value,
        year: "",
      }));
      setLevel3Placeholder(value || level3PH);
      return;
    }

    if (name === "distance") {
      const v = Number(value || 0);
      setFormData((prev) => ({ ...prev, distance: v }));
      onDistanceRangeChange(v);
      return;
    }

    // --- Special case: Level 2 → Target ---
    if (name === "levelTwo" && value === "Target") {
      // Reset Season/Year/Dates whenever Level 2 changes
      resetSeasonYearAndDates();

      setFormData((prev) => {
        const next = {
          ...prev,
          levelTwo: value,
          distance: 0,
          year: "",
          season: [],
        } as typeof prev;

        if (!level3Disabled) {
          next.levelThree = ALL_CROPS;
          setLevel3Placeholder(ALL_CROPS);
        } else {
          next.levelThree = "";
          setLevel3Placeholder(level3PH);
        }

        setClaimsCategory(value === "Claim Processed" ? claimsCategory : null);
        return next;
      });
      return;
    }

    // --- General Level 2 changes: reset Season/Year/Dates ---
    if (name === "levelTwo") {
      // Reset Season/Year/Dates whenever Level 2 changes
      resetSeasonYearAndDates();

      setFormData((prev) => {
        const next = {
          ...prev,
          levelTwo: value,
          distance: 0,
          year: "",
          season: [],
        } as typeof prev;

        if (level3Disabled) {
          next.levelThree = "";
          setLevel3Placeholder(level3PH);
        }
        setClaimsCategory(value === "Claim Processed" ? claimsCategory : null);
        return next;
      });
      return;
    }

    if (name === "claimsCategory") {
      setClaimsCategory(value || null);
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value, distance: 0 }));
  };

  const clearGridId = () => {
    onClearGridId();
    setFormData((prevState) => ({
      ...prevState,
      gridNumber: "",
      distance: 0,
    }));
    onDistanceRangeChange(0);
  };

  const handleSubmit = () => {
    const isConsolidated =
      formData.levelOne === "Consolidated Business" ||
      formData.levelOne === "Consolidated Manpower";

    const requiresLevelTwo =
      levelOneLower === "policy" ||
      levelOneLower === "claims" ||
      levelOneLower === "pospm";

    // Validation: require Level 2 for certain Level 1s
    if (requiresLevelTwo && !formData.levelTwo) {
      setError("*Please select Level 2");
      return;
    }

    // Validation: Claims → Claim Processed requires claimsCategory
    if (
      levelOneLower === "claims" &&
      formData.levelTwo === "Claim Processed" &&
      !claimsCategory
    ) {
      setError("*Please select Claims Category");
      return;
    }

    if (isConsolidated) {
      if (formData?.season?.length > 0 && !formData?.year) {
        setError("*Please select an year");
        return;
      }
    } else {
      if (formData.state === "") {
        setError("*Please select a state");
        return;
      }
      if (formData.product === "") {
        setError("*Please select a product");
        return;
      }
      if (formData?.season?.length > 0 && !formData?.year) {
        setError("*Please select an year");
        return;
      }
      if (formData.levelOne === "") {
        setError("*Please select Level 1");
        return;
      }
    }

    setError("");

    if (formData?.state) {
      dispatch(
        updateActiveView(
          isConsolidated
            ? ACTIVE_VIEW_CONSOLIDATED
            : formData?.state === COUNTRY
            ? ACTIVE_VIEW_COUNTRY
            : ACTIVE_VIEW_STATE,
        ),
      );
    }

    const payload = { ...formData, claimsCategory };
    onApplyFilters(payload);
  };

  return (
    <div className="filter-section-container">
      <div className="d-flex justify-content-end">
        <img
          alt="filter"
          className="expand-filter"
          src={isExpanded ? Up : Down}
          onClick={() => setExpanded(!isExpanded)}
        />
      </div>

      {isExpanded && (
        <>
          <div className="top-right-note">
            Note: All dates are based on the policy issue date
          </div>

          <div className="filter-section-wrapper">
            <div>
              <StateFilter
                formData={formData}
                stateList={stateList}
                districtList={districtList}
                gridId={gridId}
                handleStateChange={handleStateChangeLocal}
                handleDistrictChange={handleDistrictChangeLocal}
                handleDateChange={handleDateChange}
                handleChange={handleChange}
                setFormData={setFormData}
                statePlaceholder={statePlaceholder}
                districtPlaceholder={districtPlaceholder}
                level3Placeholder={level3Placeholder}
                clearFilters={clearFilters}
                handleSubmit={handleSubmit}
                clearGridId={clearGridId}
                minDate={minDate}
                maxDate={maxDate}
                claimsCategory={claimsCategory}
                setClaimsCategory={setClaimsCategory}
                level3Disabled={level3Disabled}
              />

              {error && <FormGroup className="error-text">{error}</FormGroup>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FilterSection;